/**
 * PermitAnalyticsPage — flow executions + citizen discovery funnel.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Home as HomeIcon,
  Event as EventIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { getDiscoveryAnalyticsApi } from '../api/applicationType.api';

const FUNNEL_LABELS = {
  portal_view: 'Portal views',
  address_validated: 'Address validated',
  zoning_check: 'Zoning checks',
  advise_run: 'Advise runs',
  summary_downloaded: 'Summaries downloaded',
  staff_opened: 'Staff opened',
};

export default function PermitAnalyticsPage({ darkMode }) {
  const [analytics, setAnalytics] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [flowRes, disc] = await Promise.all([
          fetch('/api/v1/flow-executions/permit-analytics', { credentials: 'include' })
            .then((r) => r.json())
            .catch(() => null),
          getDiscoveryAnalyticsApi({ days: 30 }).catch(() => null),
        ]);
        if (cancelled) return;
        if (flowRes?.success) setAnalytics(flowRes.analytics);
        if (disc?.analytics) setFunnel(disc.analytics);
        else if (disc?.success && disc.analytics) setFunnel(disc.analytics);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Layout darkMode={darkMode}>
        <Box sx={{ p: 4 }}>
          <LinearProgress />
        </Box>
      </Layout>
    );
  }

  const data = analytics || {
    totalExecutions: 0,
    completedExecutions: 0,
    completionRate: 0,
    byPortal: { business: 0, residential: 0, events: 0 },
    permitTypes: [],
  };

  const total = data.totalExecutions || 0;
  const byPortal = data.byPortal || {};
  const funnelRows = funnel?.funnel || [];
  const maxFunnel = Math.max(1, ...funnelRows.map((r) => r.count || 0));

  return (
    <Layout darkMode={darkMode}>
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Permit Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Citizen discovery funnel (last
          {' '}
          {funnel?.days || 30}
          {' '}
          days) and legacy permit flow executions.
        </Typography>

        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Discovery funnel
        </Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {funnelRows.map((row) => (
            <Grid item xs={12} sm={6} md={4} key={row.event}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {FUNNEL_LABELS[row.event] || row.event}
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {row.count || 0}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round(((row.count || 0) / maxFunnel) * 100)}
                    sx={{ mt: 1.5, height: 6, borderRadius: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
          {!funnelRows.length && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                No discovery events yet. Open citizen portals, validate an address, or download a summary.
              </Typography>
            </Grid>
          )}
        </Grid>

        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Flow executions
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingIcon color="primary" />
                  <Typography variant="h6">Total Executions</Typography>
                </Box>
                <Typography variant="h4" fontWeight={700}>
                  {total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckIcon color="success" />
                  <Typography variant="h6">Completion Rate</Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {data.completionRate}
                  %
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.completedExecutions}
                  {' '}
                  of
                  {' '}
                  {total}
                  {' '}
                  completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  By Portal
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" />
                    <Typography variant="body2">Business</Typography>
                    <Chip label={byPortal.business || 0} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HomeIcon fontSize="small" />
                    <Typography variant="body2">Residential</Typography>
                    <Chip label={byPortal.residential || 0} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon fontSize="small" />
                    <Typography variant="body2">Events</Typography>
                    <Chip label={byPortal.events || 0} size="small" />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Top Permit Types Requested
              </Typography>
              {data.permitTypes?.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {data.permitTypes.map(({ name, count }) => (
                    <Chip
                      key={name}
                      label={`${name} (${count})`}
                      variant="outlined"
                      size="medium"
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No permit type data yet. Run some permit discovery flows to see analytics.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}
